import { useEffect, useRef, useState } from "react";
import { Link2, Check, Mail, Share2 } from "lucide-react";
import { FaWhatsapp, FaLinkedinIn } from "react-icons/fa";
import { canonicalUrl } from "./canonicalUrl";

/**
 * Share links for a single page.
 *
 * Deliberately hand-rolled rather than a sharing plugin: AddThis is gone and
 * ShareThis-style widgets load third-party tracking on every page view, which
 * would undo the bundle work and hand visitor data to another party for a
 * feature used by a small minority. These are plain links — no script, no
 * cookies, nothing loaded until someone clicks.
 *
 * The channel order is deliberate for a B2B catalogue: WhatsApp first, because
 * forwarding a product to a colleague or an engineer is how these links
 * actually travel, then LinkedIn and email, then a copy-link fallback.
 */
const ShareRow = ({ title, label = "Share this page", className = "" }) => {
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const copyTimer = useRef(null);

  // window/navigator are only read after mount so this stays safe if the app is
  // ever prerendered.
  useEffect(() => {
    setUrl(canonicalUrl());
    setCanNativeShare(typeof navigator !== "undefined" && !!navigator.share);
    return () => clearTimeout(copyTimer.current);
  }, []);

  const flashCopied = () => {
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      flashCopied();
    } catch {
      // Clipboard API needs a secure context and permission; fall back to the
      // old selection trick rather than leaving the button dead.
      const field = document.createElement("textarea");
      field.value = url;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand("copy");
        flashCopied();
      } finally {
        document.body.removeChild(field);
      }
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, url });
    } catch {
      // The visitor dismissed the sheet — nothing to report.
    }
  };

  if (!url) return null;

  const shareText = `${title} — ${url}`;
  const iconClass =
    "flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 " +
    "text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50 " +
    "hover:text-[var(--website-primary-color)]";

  return (
    <div className={className}>
      <p className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-gray-400">
        {label}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share ${title} on WhatsApp`}
          title="Share on WhatsApp"
          className={iconClass}
        >
          <FaWhatsapp size={17} aria-hidden="true" />
        </a>

        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share ${title} on LinkedIn`}
          title="Share on LinkedIn"
          className={iconClass}
        >
          <FaLinkedinIn size={15} aria-hidden="true" />
        </a>

        <a
          href={`mailto:?subject=${encodeURIComponent(
            title
          )}&body=${encodeURIComponent(shareText)}`}
          aria-label={`Share ${title} by email`}
          title="Share by email"
          className={iconClass}
        >
          <Mail size={16} aria-hidden="true" />
        </a>

        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy link to ${title}`}
          title={copied ? "Link copied" : "Copy link"}
          className={iconClass}
        >
          {copied ? (
            <Check size={16} className="text-green-600" aria-hidden="true" />
          ) : (
            <Link2 size={16} aria-hidden="true" />
          )}
        </button>

        {/* Only worth showing where the OS provides a share sheet — on a phone
            this is the one people actually reach for. */}
        {canNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            aria-label={`Share ${title}`}
            title="Share"
            className={iconClass}
          >
            <Share2 size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {copied ? "Link copied to clipboard" : ""}
      </p>
    </div>
  );
};

export default ShareRow;
